import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../../Components/SEO/SEO';
import { getPublishedPosts, getPostsByCategory, getPostsByTag, getAllCategories } from '../../Content/posts';

// Tag Chips Component
const TagChips = ({ tags, limit = 3 }) => {
  if (!tags || tags.length === 0) return null;

  const visibleTags = tags.slice(0, limit);
  const remainingCount = tags.length - limit;

  return (
    <div className="flex items-center gap-2 mt-4">
      {visibleTags.map((tag, index) => (
        <span
          key={index}
          className="px-3 py-1 bg-gray-800 text-gray-400 text-xs
                   rounded-full hover:bg-gray-700 hover:text-pink
                   transition-colors cursor-pointer"
        >
          #{tag}
        </span>
      ))}
      {remainingCount > 0 && (
        <span className="px-3 py-1 bg-gray-800/50 text-gray-500 text-xs
                       rounded-full">
          +{remainingCount}
        </span>
      )}
    </div>
  );
};

const BlogList = () => {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    category: '',
    tag: '',
    search: '',
    contentType: '',
  });

  // Read URL query params on mount and when they change
  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam && (typeParam === 'article' || typeParam === 'blog')) {
      setFilters(prev => ({ ...prev, contentType: typeParam }));
    }
  }, [searchParams]);

  const allPosts = useMemo(() => {
    let posts = getPublishedPosts();

    if (filters.category) {
      posts = getPostsByCategory(filters.category);
    }

    if (filters.tag) {
      posts = getPostsByTag(filters.tag);
    }

    if (filters.contentType) {
      posts = posts.filter(post => post.contentType === filters.contentType);
    }

    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      posts = posts.filter(post =>
        post.title.toLowerCase().includes(searchLower) ||
        post.excerpt.toLowerCase().includes(searchLower) ||
        post.content.toLowerCase().includes(searchLower)
      );
    }

    return posts;
  }, [filters]);

  const categories = useMemo(() => getAllCategories(), []);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getContentPreview = (content, maxLength = 300) => {
    const plainText = content
      .replace(/[#[\]()*`_~]/g, '')
      .replace(/\n\n/g, '___PARAGRAPH___')           // Mark paragraph breaks
      .replace(/\n(?=[-•*]|\d+\.)/g, '___LISTITEM___') // Mark list items
      .replace(/\n/g, ' ')                            // Single newlines become spaces
      .replace(/___PARAGRAPH___/g, '\n\n')            // Restore paragraph breaks
      .replace(/___LISTITEM___/g, '\n')               // Restore list newlines
      .trim();

    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength).trim() + '...';
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <SEO
        title="Blog - E. x .O."
        description="Read the latest articles about Web3, blockchain, LUKSO, and the decentralized future"
        keywords={['blog', 'web3', 'blockchain', 'LUKSO', 'decentralized', 'E. x .O.']}
      />

      {/* Max-width container wrapper */}
      <div className="max-w-[1200px] mx-auto px-6 sm:px-8 lg:px-12 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Blog
          </h1>
          <p className="text-xl text-gray-300 font-['Space_Grotesk'] tracking-normal">
            Insights, tutorials, and updates from the E. x .O. community
          </p>
        </div>

        {/* Filters */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 mb-8">
          <div className="space-y-6">
            {/* Search */}
            <form onSubmit={handleSearch} className="relative">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full px-4 py-3 pl-12 border border-gray-600 rounded-lg
                           bg-gray-900 text-white placeholder-gray-500
                           font-['Space_Grotesk'] tracking-normal
                           focus:ring-2 focus:ring-pink focus:border-transparent
                           transition-all"
                />
                {/* Search icon */}
                <svg
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>

                {/* Clear button */}
                {(filters.search || filters.category || filters.contentType) && (
                  <button
                    type="button"
                    onClick={() => setFilters({ category: '', tag: '', search: '', contentType: '' })}
                    className="absolute right-3 top-1/2 -translate-y-1/2
                             px-3 py-1 text-sm text-gray-400 hover:text-pink
                             transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </form>

            {/* Content Type Tabs */}
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => setFilters({ ...filters, contentType: '' })}
                className={`px-4 py-2 rounded-lg font-medium font-['Space_Grotesk']
                          transition-all ${
                  filters.contentType === ''
                    ? 'bg-pink text-white shadow-lg shadow-pink/30'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                All Posts
              </button>
              <button
                onClick={() => setFilters({ ...filters, contentType: 'blog' })}
                className={`px-4 py-2 rounded-lg font-medium font-['Space_Grotesk']
                          transition-all ${
                  filters.contentType === 'blog'
                    ? 'bg-pink text-white shadow-lg shadow-pink/30'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Community Blogs
              </button>
              <button
                onClick={() => setFilters({ ...filters, contentType: 'article' })}
                className={`px-4 py-2 rounded-lg font-medium font-['Space_Grotesk']
                          transition-all ${
                  filters.contentType === 'article'
                    ? 'bg-pink text-white shadow-lg shadow-pink/30'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Technical Articles
              </button>
            </div>

            {/* Category Chips */}
            {categories.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-3
                             font-['Space_Grotesk'] tracking-normal">
                  Categories:
                </h3>
                <div className="flex gap-2 flex-wrap">
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => setFilters({
                        ...filters,
                        category: filters.category === category ? '' : category
                      })}
                      className={`px-3 py-1.5 rounded-full text-sm font-['Space_Grotesk']
                                transition-all ${
                        filters.category === category
                          ? 'bg-pink text-white shadow-md shadow-pink/30'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-gray-300'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Divider after filters */}
        <div className="border-t border-gray-800 mb-8"></div>

        {/* Posts */}
        {allPosts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-400 font-['Space_Grotesk']">
              No posts found. {filters.search && 'Try a different search term.'}
            </p>
          </div>
        ) : (
          <>
            {/* Featured Post */}
            <Link
              to={`/blog/${allPosts[0].slug}`}
              className="block group mb-8"
            >
              <article className="bg-gradient-to-br from-pink/20 via-purple/10 to-transparent
                                border border-pink/30 rounded-xl p-8
                                transition-all duration-200
                                hover:border-pink/50 hover:-translate-y-1
                                hover:shadow-xl hover:shadow-pink/20">
                {/* Featured badge */}
                <span className="inline-block px-3 py-1 bg-pink text-white text-xs
                               font-bold uppercase tracking-wider rounded-full mb-4">
                  Featured
                </span>

                {/* Title */}
                <h2 className="text-3xl md:text-4xl font-bold text-white
                             group-hover:text-pink transition-colors mb-4">
                  {allPosts[0].title}
                </h2>

                {/* Excerpt */}
                <p className="text-lg text-gray-300 font-['Space_Grotesk']
                            tracking-normal leading-relaxed mb-6 line-clamp-3 whitespace-pre-line">
                  {getContentPreview(allPosts[0].content, 400)}
                </p>

                {/* Metadata */}
                <div className="flex items-center gap-4 text-sm text-gray-400
                              font-['Space_Grotesk'] tracking-normal flex-wrap">
                  {allPosts[0].author?.name && <span>{allPosts[0].author.name}</span>}
                  <time dateTime={allPosts[0].publishedAt}>
                    {formatDate(allPosts[0].publishedAt)}
                  </time>
                  <span className="text-xs uppercase tracking-wider text-gray-500">
                    {allPosts[0].contentType}
                  </span>
                </div>

                {/* Tags */}
                <TagChips tags={allPosts[0].tags} limit={4} />
              </article>
            </Link>

            {/* Divider between featured and regular posts */}
            <div className="border-t border-gray-800 mb-8"></div>

            {/* Regular Posts */}
            <div className="space-y-6">
              {allPosts.slice(1).map((post, index) => (
                <Link
                  key={post.slug || index}
                  to={`/blog/${post.slug}`}
                  className="block group"
                >
                  <article className="bg-white/[0.04] border border-white/10 rounded-lg
                                    p-6 transition-all duration-200
                                    hover:bg-white/[0.06] hover:border-pink/30
                                    hover:-translate-y-1 hover:shadow-lg hover:shadow-pink/10">
                    {/* Title */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white
                                 group-hover:text-pink transition-colors mb-3">
                      {post.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="text-base text-gray-400 font-['Space_Grotesk']
                                tracking-normal leading-relaxed mb-4 line-clamp-2 whitespace-pre-line">
                      {getContentPreview(post.content)}
                    </p>

                    {/* Metadata */}
                    <div className="flex items-center gap-4 text-sm text-gray-500
                                  font-['Space_Grotesk'] tracking-normal flex-wrap">
                      {post.author?.name && <span>{post.author.name}</span>}
                      <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
                      <span className="text-xs uppercase tracking-wider text-gray-600">
                        {post.contentType}
                      </span>
                    </div>

                    {/* Tags */}
                    <TagChips tags={post.tags} limit={3} />
                  </article>
                </Link>
              ))}
            </div>

            {/* Post Count */}
            <div className="text-center text-sm text-gray-400 font-['Space_Grotesk'] mt-8">
              Showing {allPosts.length} {allPosts.length === 1 ? 'post' : 'posts'}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BlogList;
